// Auto-retention (szzt-spec.md §16, Appendix B — deferred knob).
//
// §16 default is keep-all-versions; auto-retention is the optional policy layered OVER the manual
// `planPrune`/`prune` mechanism (Appendix B: "Automatic retention pruning (keep-last-N or TTL over
// the §16 manual-prune mechanism)"). This module adds only the SELECTION policy — which historical
// deployments to prune — and reuses the existing pure planner + executor unchanged: `planRetention`
// picks the deployment URIs, then those feed `planPrune` (or the `planRetentionPrune` one-call
// convenience) and the same `prune()` executor writes them. No new record shapes, no new write path.
//
// The one invariant that must survive every policy: the active version (`site.current`) is NEVER a
// prune target, even when a rollback (§8) has repointed `current` onto a version older than the
// retention window. Retention selects targets from the SUPPLIED graph and is deterministic + pure;
// the executor still re-reads `site.current` and refuses (`PruneActiveError`) at write time, so a
// concurrent rollback landing after planning is still caught downstream.

import { planPrune, type DeploymentView, type PrunePlan } from "./prune.ts";

/**
 * A deployment as the retention planner needs to see it: its record URI and the record's `createdAt`
 * (ISO 8601, the `app.szzt.deployment` `createdAt` field). Enumerating a repo's deployments
 * (com.atproto.repo.listRecords) is the caller's job — the same seam boundary `planPrune` keeps.
 */
export interface RetentionCandidate {
  /** AT-URI of the `app.szzt.deployment` record. */
  deploymentUri: string;
  /** ISO 8601 timestamp from the deployment record's `createdAt`. */
  createdAt: string;
}

/** Keep the N most-recent versions (current counted); prune the rest. See {@link planRetention}. */
export interface KeepLastInput {
  /** AT-URI of the active deployment (`site.current.uri`). Always retained (§16). */
  currentDeploymentUri: string;
  /** Every deployment of the site to consider, INCLUDING the current one. */
  deployments: readonly RetentionCandidate[];
  /** Retain the `keep` most-recent versions by `createdAt`; must be a non-negative integer. */
  keep: number;
}

/** Prune versions older than `maxAgeMs`; retain the rest. See {@link planRetention}. */
export interface MaxAgeInput {
  /** AT-URI of the active deployment (`site.current.uri`). Always retained (§16). */
  currentDeploymentUri: string;
  /** Every deployment of the site to consider, INCLUDING the current one. */
  deployments: readonly RetentionCandidate[];
  /** Prune any version whose `createdAt` is strictly older than `now - maxAgeMs`; non-negative. */
  maxAgeMs: number;
  /** Injected clock (matches publish's `now` convention); defaults to `() => new Date()`. */
  now?: () => Date;
}

/** Input to {@link planRetention}: exactly one of the keep-last-N or max-age policies. */
export type RetentionInput = KeepLastInput | MaxAgeInput;

/** The retention selection: which deployment URIs to prune, and which the policy keeps. */
export interface RetentionPlan {
  /** Deployment URIs the policy selects for pruning — feed these to {@link planPrune} as `targets`. */
  prune: string[];
  /** Deployment URIs the policy retains (always includes `currentDeploymentUri` when supplied). */
  retained: string[];
}

/** Descending string comparison for a deterministic tiebreak when two `createdAt` values collide. */
function cmpUriDesc(a: string, b: string): number {
  return a < b ? 1 : a > b ? -1 : 0;
}

/**
 * keep-last-N: retain the `keep` most-recent deployments by `createdAt` (current counted toward the
 * budget), and ALWAYS retain the current version and any deployment with an unparseable `createdAt`
 * (fail-safe — never prune a version we cannot date). Returns the set of URIs to prune.
 */
function selectKeepLast(
  deployments: readonly RetentionCandidate[],
  currentDeploymentUri: string,
  keep: number,
): Set<string> {
  if (!Number.isInteger(keep) || keep < 0) {
    throw new RangeError(`retention keep must be a non-negative integer, got ${keep}`);
  }
  // `forceRetain` always holds the current version, plus any undatable deployment (fail-safe).
  const forceRetain = new Set<string>([currentDeploymentUri]);
  const datable: { uri: string; t: number }[] = [];
  for (const d of deployments) {
    const t = Date.parse(d.createdAt);
    if (Number.isNaN(t)) {
      forceRetain.add(d.deploymentUri);
      continue;
    }
    datable.push({ uri: d.deploymentUri, t });
  }
  // Most-recent first; URI descending breaks ties so the selection is deterministic.
  datable.sort((a, b) => b.t - a.t || cmpUriDesc(a.uri, b.uri));

  const keepSet = new Set(forceRetain);
  for (let i = 0; i < Math.min(keep, datable.length); i++) keepSet.add(datable[i]!.uri);

  const prune = new Set<string>();
  for (const d of datable) if (!keepSet.has(d.uri)) prune.add(d.uri);
  return prune;
}

/**
 * max-age (TTL): prune any deployment whose `createdAt` is STRICTLY older than the cutoff
 * (`now - maxAgeMs`); a deployment exactly at the cutoff is retained. The current version and any
 * undatable deployment are always retained (fail-safe). Returns the set of URIs to prune.
 */
function selectByAge(
  deployments: readonly RetentionCandidate[],
  currentDeploymentUri: string,
  maxAgeMs: number,
  nowDate: Date,
): Set<string> {
  if (!Number.isFinite(maxAgeMs) || maxAgeMs < 0) {
    throw new RangeError(`retention maxAgeMs must be a non-negative finite number, got ${maxAgeMs}`);
  }
  const cutoff = nowDate.getTime() - maxAgeMs;
  const prune = new Set<string>();
  for (const d of deployments) {
    if (d.deploymentUri === currentDeploymentUri) continue; // current always retained
    const t = Date.parse(d.createdAt);
    if (Number.isNaN(t)) continue; // undatable → fail-safe retain
    if (t < cutoff) prune.add(d.deploymentUri);
  }
  return prune;
}

/**
 * Select which historical deployments an auto-retention policy prunes (§16, Appendix B). PURE over
 * the supplied graph. The current version and any deployment with an unparseable `createdAt` are
 * never selected. Feed {@link RetentionPlan.prune} to {@link planPrune} as its `targets` (or use
 * {@link planRetentionPrune} to do both in one call).
 */
export function planRetention(input: RetentionInput): RetentionPlan {
  const { currentDeploymentUri, deployments } = input;
  const toPrune =
    "keep" in input
      ? selectKeepLast(deployments, currentDeploymentUri, input.keep)
      : selectByAge(deployments, currentDeploymentUri, input.maxAgeMs, (input.now ?? (() => new Date()))());

  const prune: string[] = [];
  const retained: string[] = [];
  for (const d of deployments) {
    if (toPrune.has(d.deploymentUri)) prune.push(d.deploymentUri);
    else retained.push(d.deploymentUri);
  }
  return { prune, retained };
}

/** A {@link DeploymentView} carrying the `createdAt` {@link planRetentionPrune} needs to order by. */
export interface RetainableDeployment extends DeploymentView {
  /** ISO 8601 timestamp from the deployment record's `createdAt`. */
  createdAt: string;
}

/** keep-last-N variant of {@link planRetentionPrune}'s input. */
export interface KeepLastPruneInput {
  currentDeploymentUri: string;
  deployments: readonly RetainableDeployment[];
  keep: number;
}

/** max-age variant of {@link planRetentionPrune}'s input. */
export interface MaxAgePruneInput {
  currentDeploymentUri: string;
  deployments: readonly RetainableDeployment[];
  maxAgeMs: number;
  now?: () => Date;
}

/** Input to {@link planRetentionPrune}: exactly one of the keep-last-N or max-age policies. */
export type RetentionPruneInput = KeepLastPruneInput | MaxAgePruneInput;

/**
 * One-call convenience: run {@link planRetention} to pick the prune targets, then {@link planPrune}
 * over the same deployment graph to produce the executable {@link PrunePlan} (delete writes +
 * releasable blobs). Hand the result straight to `prune()`. The current version is never pruned.
 */
export function planRetentionPrune(input: RetentionPruneInput): PrunePlan {
  const retention =
    "keep" in input
      ? planRetention({
          currentDeploymentUri: input.currentDeploymentUri,
          deployments: input.deployments,
          keep: input.keep,
        })
      : planRetention({
          currentDeploymentUri: input.currentDeploymentUri,
          deployments: input.deployments,
          maxAgeMs: input.maxAgeMs,
          ...(input.now ? { now: input.now } : {}),
        });
  return planPrune({
    currentDeploymentUri: input.currentDeploymentUri,
    deployments: [...input.deployments],
    targets: retention.prune,
  });
}
